import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { TipsCard } from '@eika/card';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Link, LinkType } from '@eika/link';

import { useConfig } from '../../config';
import { getSkademeldingstypeFromQueryParam } from '../../meldskade/reducer/skadetype';
import { AppState } from '../../reducers';
import { useWindowScrollTop } from '../../sporsmalsflyt/hooks';
import { KundeforholdType } from '../../types/generated-types';
import CustomerFeedbackLink from '../sporreskjema-button/customer-feedback-link';
import {
    getSkadedetaljerUrl,
    skadetypeHasMultipleUndertegnereForSigneringsoppdrag,
    skadetypeHasSingleUndertegnerForSigneringsoppdrag,
} from './kvittering-utils';

import './skademelding-kvittering.scss';

interface KvitteringTekstProps {
    isSkadeApnetOnPrivateInnmelder: boolean;
    kundeforholdType: KundeforholdType;
}

const KvitteringTekst: FC<KvitteringTekstProps> = ({ isSkadeApnetOnPrivateInnmelder, kundeforholdType }) => {
    const location = useLocation();
    const skadetype = getSkademeldingstypeFromQueryParam(location.search);
    const isKundeBM = kundeforholdType === KundeforholdType.BMKunde;

    if (skadetypeHasMultipleUndertegnereForSigneringsoppdrag(skadetype, isKundeBM)) {
        return (
            <>
                <p>
                    Vi behøver en fullmakt for å innhente opplysninger fra de instanser som er oppgitt i skademeldingen.
                    Fullmakten skal signeres av forsikringstaker dersom forsikrede er under 18 år. Forsikringstaker vil
                    motta en e-post med link til signering.
                </p>
                <p>Forsikrede som har fylt 16 år må avgi eget samtykke og vil motta en sms vedrørende dette.</p>
                <p>Vi vil starte behandling av kravet så snart nødvendig fullmakt er gitt.</p>
            </>
        );
    }

    if (skadetypeHasSingleUndertegnerForSigneringsoppdrag(skadetype)) {
        return (
            <>
                <p>
                    Vi behøver en fullmakt for å innhente opplysninger fra de instanser som er oppgitt i skademeldingen.
                    Du vil motta en e-post med link til signering.
                </p>
                <p>Vi vil starte behandling av kravet så snart nødvendig fullmakt er gitt.</p>
            </>
        );
    }

    return isSkadeApnetOnPrivateInnmelder ? (
        <p>Nå kan du bare lene deg tilbake og slappe av, så fikser vi resten.</p>
    ) : (
        <p>
            Hvis vi trenger mer informasjon tar vi kontakt med deg. Husk å ta vare på kvitteringer, og gjerne ta bilder
            av skaden hvis det er relevant.
        </p>
    );
};

const SkademeldingKvittering: FC = () => {
    const { minside, folgskade } = useConfig().links;
    const windowScrollTop = useWindowScrollTop();
    windowScrollTop();
    const { skademeldingResult } = useSelector((state: AppState) => state.skademelding);

    const isSkadeApnetOnPrivateInnmelder =
        skademeldingResult?.skadeApnetOnInnmelder === true &&
        skademeldingResult?.skadenummer &&
        skademeldingResult?.kundeforholdType === KundeforholdType.PMKunde;

    return (
        <div className="skademelding-kvittering">
            <div className="ettersending-kvittering__checkmark-wrapper">
                <CheckmarkAnimation />
            </div>
            <h1 className="typography-h1__light">Skademeldingen er sendt inn</h1>
            <KvitteringTekst
                isSkadeApnetOnPrivateInnmelder={isSkadeApnetOnPrivateInnmelder}
                kundeforholdType={skademeldingResult.kundeforholdType}
            />
            {isSkadeApnetOnPrivateInnmelder && (
                <div className="skademelding-kvittering__tips-card">
                    <TipsCard
                        label="Tips"
                        title="Hold deg oppdatert på skadesaken"
                        text="Du kan følge behandlingen av skaden og ettersende dokumentasjon."
                        button={
                            <Link
                                linkType={LinkType.BUTTON_STANDARD_POSITIVE}
                                href={getSkadedetaljerUrl(folgskade.url, skademeldingResult.skadenummer.value)}
                            >
                                Se skadesaken
                            </Link>
                        }
                    />
                </div>
            )}
            <Link
                linkType={LinkType.BUTTON_STANDARD_NEGATIVE}
                href={minside.url}
                className="skademelding-kvittering__min-side-link"
            >
                Til forsikringsoversikten
            </Link>
            <CustomerFeedbackLink />
        </div>
    );
};

export default SkademeldingKvittering;
